(() => {
  // app/scripts/components/CardProduct.js
  var { moneyFormat } = theme.settings;
  var {
    getRegionData,
    countDecimals,
    calculateRegionInventoryTotal,
    getPickupQuantity,
    addQuantityAndUnitPrice,
    getEcoFee,
    getCustomerProvinceCode
  } = theme.utils;
  var CardProduct = class extends HTMLElement {
    _price = null;
    _source = null;
    _b2b_key = null;
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.quantity = this.querySelector("quantity-input");
      this.JSONData = JSON.parse(this.querySelector("script[data-json]")?.textContent || "{}");
      this.unitPriceLabel = this.querySelector(".js-unit-price");
      this.selectRegionButton = this.querySelector(".js-select-region");
      this.pricingData = this.JSONData.pricing;
      this.priceLabel = this.querySelector(".js-price-value");
      this.ecoFeeLabels = this.querySelectorAll(".js-eco-fee");
      this.ecoFeeData = this.JSONData.ecoFee;
      this.currentEcoFee = 0;
      this.erpNumber = this.dataset.erpNumber;
      this.oversize = this.dataset.oversize;
      this.PCProducts = this.JSONData.PCProducts;
      this.myPartInput = this.querySelector("add-my-part");
      if (theme.customer.isB2B) {
        const selectedRegion = getRegionData();
        const foundEcoFee = this.resolveEcoFee(selectedRegion);
        this.updateEcoFee(foundEcoFee);
        this.updateLocationName(theme.preferredLocation?.name);
      }
      this.initButtonEvents();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.updateLayoutByRegion = this.updateLayoutByRegion.bind(this);
      this.handlePreferredLocationChange = this.handlePreferredLocationChange.bind(this);
    }
    set price(price) {
      if (isNaN(price)) {
        console.error("Price is not a number");
        return;
      }
      this._price = Number(price);
      this.updatePrice(this._price);
    }
    get price() {
      return this._price + this.currentEcoFee;
    }
    get source() {
      return this._source;
    }
    set source(source) {
      this._source = source;
    }
    get b2b_key() {
      return this._b2b_key;
    }
    set b2b_key(key) {
      this._b2b_key = key;
    }
    getCurrentQuantity() {
      const quantity = Number.parseInt(this.quantity?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }
    async connectedCallback() {
      await customElements.whenDefined("quantity-input");
      this.initEvents();
      if (theme.customer.isB2B) {
        window.addEventListener("preferredLocation:change", this.handlePreferredLocationChange);
        this.quantity?.onChange(this.handleQuantityChange.bind(this));
      }
    }
    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.handleSubmit);
      window.removeEventListener("regionSelected", this.updateLayoutByRegion);
      window.removeEventListener("preferredLocation:change", this.handlePreferredLocationChange);
    }
    initButtonEvents() {
      this.selectRegionButton?.addEventListener(
        "click",
        (e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("open-select-region"));
        },
        {}
      );
    }
    get properties() {
      const properties = { _source: this._source };
      if (theme.customer.isB2B) {
        properties._b2b_key = this._b2b_key;
        properties._b2b_price = String(this._price);
        this.myPartInput?.value && (properties._my_part_number = this.myPartInput.value);
      }
      if (this.currentEcoFee) {
        properties._ecofee = this.currentEcoFee;
      }
      if (countDecimals(this._price) > 2) {
        properties._quantity = this.quantity.value;
      }
      return properties;
    }
    async handleSubmit(event) {
      try {
        event.preventDefault();
        this.setState("adding");
        const formData = new FormData(this.form);
        const quantity = +formData.get("quantity");
        const id = this.form.id.value;
        formData.append("sections", "drawer-cart-items,header-cart-popup");
        formData.append("sections_url", `${theme.routes.searchUrl}?q=[variants.id:${id}]`);
        formData.append("properties[_source]", this._source);
        if (theme.customer.isB2B) {
          this.myPartInput?.value ? formData.append("properties[_my_part_number]", this.myPartInput.value) : formData.delete("properties[_my_part_number]");
          formData.append("properties[_b2b_price]", String(this._price));
          formData.append("properties[_b2b_key]", this._b2b_key);
        }
        if (this.currentEcoFee) {
          formData.append("properties[_ecofee]", this.currentEcoFee);
        }
        if (countDecimals(this._price) > 2) {
          addQuantityAndUnitPrice(formData, this._price, quantity);
        }
        const dataToChange = await Cart.checkItemExists({
          id,
          quantity,
          erpNumber: this.erpNumber,
          price: this._price
        });
        if (dataToChange) {
          await Cart.change({
            data: {
              ...dataToChange,
              sections: formData.get("sections"),
              sections_url: formData.get("sections_url")
            },
            action: "add"
          });
        } else {
          await Cart.add(formData);
        }
        if (window.theme.routes.pageType == "cart") {
          window.location.reload();
        }
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async initEvents() {
      this.form?.addEventListener("submit", this.handleSubmit);
      if (theme.customer.isB2B)
        return;
      const regionSelector = document.querySelector("select-region");
      if (regionSelector) {
        await customElements.whenDefined("select-region");
        window.addEventListener("regionSelected", this.updateLayoutByRegion);
        window.addEventListener("regionDetected", this.updateLayoutByRegion);
      }
      const selectedRegion = getRegionData();
      selectedRegion && this.updateLayoutByRegion({ detail: { regionData: selectedRegion } });
      this.setState("fetched-price");
    }
    handlePreferredLocationChange(event) {
      const { preferredLocation } = event.detail;
      const priceInventory = window.PriceInventory.storage.find(this.erpNumber);
      this.updateLocationName(preferredLocation.name);
      priceInventory && this.updateB2BLayout(priceInventory?.data, preferredLocation);
      const foundRegion = theme.regionData.find((region) => region.title == preferredLocation.region);
      const foundEcoFee = this.resolveEcoFee(foundRegion, preferredLocation?.pc_number);
      this.updateEcoFee(foundEcoFee);
    }
    updateLayoutByRegion({ detail }) {
      const selectedRegion = detail.regionData;
      const { customer_id, location_id } = selectedRegion;
      const foundEcoFee = this.resolveEcoFee(selectedRegion);
      const foundShopLocation = theme.shopLocations.find((location) => location.id == location_id);
      const foundPCProduct = this.findPCProductAndUpdateLayout(foundShopLocation?.pc_number);
      const available = this.oversize === "false" && !!foundPCProduct?.InStock;
      const foundPricing = this.pricingData.find((pricing) => pricing.CustomerID == customer_id);
      this.setState(available ? "stock-available" : "stock-unavailable");
      this.updateEcoFee(foundEcoFee);
      if (foundPricing) {
        const sorted = [...foundPricing.Pricing || []].sort((a, b) => a.QtyBreak - b.QtyBreak);
        const { Pricing = 0, Source } = sorted[0] || {};
        const price = Number(Pricing) || 0;
        if (price) {
          this.price = price;
          this._source = Source;
          this.setState("price-available");
        } else {
          this.setState("price-unavailable");
        }
      } else {
        this.setState("price-unavailable");
      }
    }
    updateB2BLayout(priceInventory, shopLocation) {
      const { Quantity, WarehousesQuantities = [] } = priceInventory || {};
      const { b2bDeliveryQuantity } = theme.settings;
      const pickupQuantity = getPickupQuantity(WarehousesQuantities, shopLocation, Quantity);
      const totalInventoryQuantity = b2bDeliveryQuantity === "region" ? calculateRegionInventoryTotal(WarehousesQuantities, shopLocation, Quantity) : pickupQuantity;
      const pricing = PriceInventory.findPricingByQtyBreak(this.erpNumber, this.quantity?.value || 1);
      this.findPCProductAndUpdateLayout(shopLocation.pc_number);
      this.setState("fetched-price");
      if (!pricing) {
        this.setState("price-unavailable");
        return;
      }
      const { Pricing, Source, key } = pricing;
      this.price = +Pricing;
      this.source = Source;
      this.b2b_key = key;
      if (+Pricing === 0) {
        this.setState("price-unavailable");
        return;
      }
      if (totalInventoryQuantity) {
        this.updateTotalInventory(totalInventoryQuantity);
        if (pickupQuantity > 0) {
          this.updatePickupInventory(pickupQuantity);
          this.setState("pickup-available");
        } else {
          this.setState("pickup-unavailable");
        }
      } else {
        this.setState("stock-unavailable");
      }
    }
    findPCProductAndUpdateLayout(pcNumber) {
      if (!pcNumber)
        return;
      const foundPCProduct = this.PCProducts.find((pcProduct) => pcProduct.WarehouseName == pcNumber);
      this.updateQuantityStep(foundPCProduct?.MultipleSaleQty);
      this.updateMinQuantity(foundPCProduct?.MinimumOrderQty);
      return foundPCProduct;
    }
    resolveEcoFee(region, preferredLocationId) {
      const provinceCode = getCustomerProvinceCode(region, preferredLocationId);
      return getEcoFee(this.ecoFeeData, region, provinceCode);
    }
    updateEcoFee(ecoFee) {
      this.currentEcoFee = Number(ecoFee) || 0;
      if (!this.ecoFeeLabels.length)
        return;
      const ecoFeeMoney = theme.settings.moneyFormat.replace("{{amount}}", this.currentEcoFee.toFixed(2));
      this.ecoFeeLabels.forEach((label) => {
        if (this.currentEcoFee) {
          label.textContent = label.dataset.message.replace("{{money}}", ecoFeeMoney);
          label.classList.remove("hidden");
        } else {
          label.classList.add("hidden");
        }
      });
    }
    updatePrice(price) {
      if (!this.priceLabel)
        return;
      const totalPrice = Math.round((price + this.currentEcoFee) * 100) / 100;
      this.priceLabel.textContent = moneyFormat.replace("{{amount}}", totalPrice.toFixed(2));
      this.handleAfterPriceUpdate();
    }
    updateUnitPrice(newUnitPrice) {
      if (!this.unitPriceLabel)
        return;
      if (newUnitPrice) {
        this.unitPriceLabel.textContent = `/ ${newUnitPrice}`;
        this.unitPriceLabel.classList.remove("hidden");
      } else {
        this.unitPriceLabel.classList.add("hidden");
      }
      this.handleAfterPriceUpdate();
    }
    updateTotalInventory(quantity) {
      this.querySelectorAll(".js-total-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updatePickupInventory(quantity) {
      this.querySelectorAll(".js-pickup-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updateQuantityStep(step) {
      if (!this.quantity || !step)
        return;
      this.quantity.updateStep(step);
      if (step > 1) {
        const messageElm = this.querySelector(".js-sold-multiples-message");
        if (messageElm) {
          messageElm.textContent = messageElm.dataset.message.replace("{{ sold_multiples }}", step);
        }
        this.setState("sold-multiples");
      } else {
        this.setState("sold-single");
      }
    }
    updateMinQuantity(number) {
      if (!this.quantity || !number)
        return;
      this.quantity.input.min = number;
    }
    updateLocationName(name) {
      const locationNameElm = this.querySelector(".js-location-name");
      if (!locationNameElm)
        return;
      const locationId = window.gic_customer.WarehouseName;
      const locationName = name || theme.shopLocations.find((location) => location.pc_number == locationId)?.name;
      locationNameElm.textContent = locationName;
    }
    handleQuantityChange(newQuantity) {
      const pricing = PriceInventory.findPricingByQtyBreak(this.erpNumber, newQuantity);
      if (!pricing)
        return;
      if (this._price != pricing.Pricing) {
        this.price = pricing.Pricing;
      }
    }
    setState(state) {
      switch (state) {
        case "stock-unavailable":
          this.classList.add("stock-unavailable");
          break;
        case "stock-available":
          this.classList.remove("stock-unavailable");
          break;
        case "pickup-unavailable":
          this.classList.add("pickup-unavailable");
          break;
        case "pickup-available":
          this.classList.remove("pickup-unavailable");
          break;
        case "sold-multiples":
          this.classList.add("sold-multiples");
          break;
        case "sold-single":
          this.classList.remove("sold-multiples");
          break;
        case "fetched-price":
          this.classList.remove("is-fetching-price");
          break;
        case "price-not-found":
          this.classList.remove("is-fetching-price");
          this.classList.add("price-missing");
          break;
        case "price-unavailable":
          this.classList.add("price-unavailable");
          break;
        case "price-available":
          this.classList.remove("price-unavailable");
          break;
        case "deleting":
          this.classList.add("is-loading", "is-deleting");
          break;
        case "adding":
          this.classList.add("is-loading", "is-atc-loading");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-atc-loading", "is-deleting");
      }
    }
    // Override this method in child class
    handleAfterPriceUpdate() {
    }
  };

  // app/scripts/components/LineItemNote.js
  var LineItemNote = class extends HTMLElement {
    constructor() {
      super();
      this.triggerButton = this.querySelector(".js-trigger-button");
      this.noteElm = this.querySelector("[name='note']");
      this.triggerButton.addEventListener("click", () => {
        this.classList.add("is-open");
        this.noteElm.focus();
      });
      this.noteElm.addEventListener("blur", (event) => {
        if (!this.noteElm.value) {
          this.classList.remove("is-open");
        }
      });
    }
  };
  customElements.define("line-item-note", LineItemNote);

  // app/scripts/components/CardLineItem.js
  var CardLineItem = class extends CardProduct {
    constructor() {
      super();
      this.totalPrice = this.querySelector(".js-total-price");
      this.key = this.dataset.key;
      this.noteElm = this.querySelector("[name='note']");
      this.triggerButton = this.querySelector("[name='trigger-note']");
      this.initRemoveButtons();
      this.listenQuantityChange();
      this.initNote();
    }
    initNote() {
      this.noteElm?.addEventListener("change", (event) => {
        this.dispatch({ type: "update-note", value: event.target.value, key: this.key });
      });
    }
    async listenQuantityChange() {
      await customElements.whenDefined("quantity-input");
      this.quantity.onChange((newValue) => {
        this.dispatch({ type: "update-quantity", value: newValue, key: this.key });
      });
    }
    initRemoveButtons() {
      this.querySelectorAll(".js-remove-button").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.dispatch({ type: "remove-line-item", key: this.key });
        });
      });
    }
    dispatch(value) {
      this.dispatchEvent(new CustomEvent("cart-change", { detail: value, bubbles: true }));
    }
  };

  // app/scripts/cart-component.js
  var CartComponent = class extends HTMLElement {
    constructor() {
      super();
      this.sectionIds = this.dataset.sectionIds;
      this.form = this.querySelector("form");
      this.addEventListener("cart-change", this.onChange.bind(this));
    }
    async connectedCallback() {
      if (!await Cart.hasCheckoutPermission()) {
        this.classList.add("requires-approval");
      }
      this.classList.remove("is-loading-role");
    }
    handleUpdateQuantity = theme.debounceTime(async (type, key) => {
      const cardLineItem = this.findLineItem(key);
      const quantity = +cardLineItem.quantity.value;
      let dataToChange;
      try {
        dataToChange = await Cart.checkItemExists({
          id: key,
          quantity,
          erpNumber: cardLineItem.erpNumber,
          price: cardLineItem.price,
          sumLineItemCount: false
        });
      } catch (error) {
        if (error.pricingUnavailable) {
          console.warn("[CartComponent] Cannot update line item \u2014 pricing unavailable");
          return;
        }
        throw error;
      }
      if (!dataToChange)
        return;
      await this.handleCartChange({ ...dataToChange, sections: this.sectionIds }, type);
    }, 500);
    async onChange(event) {
      const { type, key, value } = event.detail;
      switch (type) {
        case "update-quantity":
          return this.handleUpdateQuantity(type, key);
        case "remove-line-item":
          return this.handleCartChange({ id: key, quantity: 0, sections: this.sectionIds }, type);
        case "update-note": {
          const lineItem = await Cart.findLineItem(key);
          return this.handleCartChange(
            {
              id: key,
              properties: { ...lineItem.properties, _Comment: value },
              sections: this.sectionIds
            },
            type
          );
        }
      }
    }
    async handleCartChange(data, type) {
      try {
        this.loading(true);
        const response = await Cart.change({ data });
        this.updateLayout(response, type);
      } catch (error) {
        console.error("[CartComponent] handleCartChange error:", error);
        throw error;
      } finally {
        this.loading(false);
      }
    }
    setState(state) {
      switch (state) {
        case "loading":
          this.classList.add("is-loading");
          break;
        case "request-approval":
          this.classList.add("is-request-approval");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-request-approval");
          break;
      }
    }
    loading(isLoading) {
      this.classList.toggle("is-loading", isLoading);
    }
    updateLayout(data, type) {
      if (!data?.sections)
        return;
      const layout = new DOMParser().parseFromString(data.sections[this.id], "text/html");
      const newCartComponent = layout.querySelector("cart-component");
      this.querySelectorAll(".js-cart-update").forEach((elm) => {
        const foundElement = layout.getElementById(elm.id);
        if (foundElement) {
          elm.innerHTML = foundElement.innerHTML;
        }
      });
      this.dataset.count = newCartComponent.dataset.count;
      const cartRecentlyViewed = document.querySelector(".cart-recently-viewed");
      if (cartRecentlyViewed) {
        const hasItems = this.itemCount() > 0;
        cartRecentlyViewed.classList.toggle("hidden", hasItems);
        cartRecentlyViewed.classList.toggle("block", !hasItems);
      }
      window.PriceInventory.fetchLivePriceInventory();
    }
    itemCount() {
      return Number(this.dataset.count || 0);
    }
    findLineItem(key) {
      return this.querySelector(`card-line-item[data-key="${key}"]`);
    }
  };
  customElements.define("cart-component", CartComponent);
  customElements.define("card-line-item", CardLineItem);
})();
